import { useEffect, useRef } from 'react'

/** The message embed pages send up to the page that holds them, so the loader script can resize the frame. */
export const EMBED_RESIZE_MESSAGE = 'certslate:resize'

/** The message the loader script sends down to a frame that may have reported its height before the script was listening. */
export const EMBED_RESIZE_REQUEST = 'certslate:resize-request'

/**
 * For pages shown inside a customer's iframe: keeps the page background clear, and tells the parent page how tall
 * the content is (now, whenever it changes, and whenever the parent asks) so the frame can fit it instead of scrolling.
 * Attach the returned ref to the element that wraps everything on the page. Does nothing when the page is not in a frame.
 */
export function useEmbedResize() {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const previousBackground = document.body.style.backgroundColor
    document.body.style.backgroundColor = 'transparent'

    const el = ref.current
    let observer: ResizeObserver | undefined
    let onMessage: ((e: MessageEvent) => void) | undefined
    if (el && window.parent !== window) {
      let last = -1
      const send = () => {
        const height = Math.ceil(el.getBoundingClientRect().height)
        if (height === last) return
        last = height
        // Only a number is sent, so it is safe to address any parent.
        window.parent.postMessage({ type: EMBED_RESIZE_MESSAGE, height }, '*')
      }
      observer = new ResizeObserver(send)
      observer.observe(el)
      send()

      onMessage = (e: MessageEvent) => {
        if (e.source !== window.parent || e.data?.type !== EMBED_RESIZE_REQUEST) return
        last = -1
        send()
      }
      window.addEventListener('message', onMessage)
    }

    return () => {
      observer?.disconnect()
      if (onMessage) window.removeEventListener('message', onMessage)
      document.body.style.backgroundColor = previousBackground
    }
  }, [])

  return ref
}
